"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { captureClientException } from "@/shared/lib/monitoring/capture-client"

export type ImageStatus = "loading" | "loaded" | "error"

interface UseImageFallbackOptions {
  /** Original image URL. */
  src: string
  /** Observability scope, e.g. "character-card-avatar". */
  scope: string
  /** Safe metadata only: never user-authored content. */
  extra?: Record<string, unknown>
}

interface UseImageFallbackResult {
  status: ImageStatus
  /**
   * Apply as the `key` on the <Image>. It changes once after a transient
   * failure to remount the element and re-request the same optimizer URL.
   */
  reloadKey: number
  onLoad: () => void
  onError: () => void
}

// next/image routes every request through the shared image optimizer. Retry
// once by remounting the <Image>, then let the caller render a fallback.
// Failures are captured with safe metadata only so they become observable.
export function useImageFallback({
  src,
  scope,
  extra,
}: UseImageFallbackOptions): UseImageFallbackResult {
  const [status, setStatus] = useState<ImageStatus>("loading")
  const [reloadKey, setReloadKey] = useState(0)
  const retriedRef = useRef(false)

  // Keep capture context current without churning the memoized handlers.
  const scopeRef = useRef(scope)
  const extraRef = useRef(extra)
  scopeRef.current = scope
  extraRef.current = extra

  // Reset when the source changes (e.g. gallery navigation) so each image
  // gets its own retry budget and fresh status.
  useEffect(() => {
    retriedRef.current = false
    setStatus("loading")
    setReloadKey(0)
  }, [src])

  const onLoad = useCallback(() => setStatus("loaded"), [])

  const onError = useCallback(() => {
    if (!retriedRef.current) {
      retriedRef.current = true
      setStatus("loading")
      setReloadKey((key) => key + 1)
      return
    }
    setStatus("error")
    captureClientException(
      new Error(`image failed to load: ${scopeRef.current}`),
      {
        scope: scopeRef.current,
        extra: { ...(extraRef.current ?? {}), image_status: "error" },
      }
    )
  }, [])

  return { status, reloadKey, onLoad, onError }
}
